import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_BASE = process.env.REACT_APP_APP_BASE_URL || 'https://geniusoneplus.com';

// Sprint N — Admin broadcast composer.
// Reads /api/admin/broadcasts/list, lets the founder pick a message +
// language + photo card, copies text-plus-image-URL to clipboard, and logs
// the send to activity_feed as an audit trail.
export default function AdminBroadcast() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(1);
  const [lang, setLang] = useState('en');
  const [cardId, setCardId] = useState(1);
  const [flash, setFlash] = useState(null);
  const [sentLog, setSentLog] = useState([]);

  const jwt = typeof window !== 'undefined' ? localStorage.getItem('gop_admin_jwt') : null;
  const headers = jwt ? { Authorization: `Bearer ${jwt}` } : {};

  useEffect(() => {
    axios.get(`${API}/admin/broadcasts/list`, { headers })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Could not load broadcasts. Sign in as admin first.'));
    axios.get(`${API}/admin/broadcasts/sent-log`, { headers })
      .then(r => setSentLog(r.data.sent || []))
      .catch(() => setSentLog([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = useMemo(() => {
    if (!data) return null;
    return data.broadcasts.find(b => b.id === selectedId) || data.broadcasts[0];
  }, [data, selectedId]);

  const currentCard = useMemo(() => {
    if (!data) return null;
    return data.photo_cards.find(c => c.id === cardId) || data.photo_cards[0];
  }, [data, cardId]);

  const message = current ? current[lang] : '';
  const imageUrl = currentCard ? `${APP_BASE}${currentCard.path}` : '';
  const composed = message + (currentCard ? `\n\n${imageUrl}` : '');

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(composed);
      setFlash('Copied to clipboard \u2014 paste into WhatsApp Business.');
      setTimeout(() => setFlash(null), 3000);
    } catch (_e) {
      setFlash('Clipboard blocked. Select the text below manually.');
    }
  };

  const openInWhatsApp = () => {
    const encoded = encodeURIComponent(composed);
    window.open(`https://wa.me/?text=${encoded}`, '_blank', 'noopener');
  };

  const markSent = async () => {
    if (!current) return;
    try {
      await axios.post(`${API}/admin/broadcasts/mark-sent`,
        { broadcast_id: current.id, language: lang, card_id: currentCard?.id },
        { headers });
      setFlash(`Logged: broadcast #${current.id} (${lang.toUpperCase()}).`);
      const r = await axios.get(`${API}/admin/broadcasts/sent-log`, { headers });
      setSentLog(r.data.sent || []);
    } catch (err) {
      setFlash(`Log failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6" data-testid="admin-broadcast-error">
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-red-300">
            <CardHeader>
              <CardTitle className="text-red-700">Broadcasts unavailable</CardTitle>
            </CardHeader>
            <CardContent><p className="text-sm">{error}</p></CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-6 text-sm text-slate-500">Loading broadcasts\u2026</div>;

  const LANGS = [
    { code: 'en', label: 'English' },
    { code: 'si', label: 'සිංහල' },
    { code: 'ta', label: 'தமிழ்' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="admin-broadcast-page">
      <div className="max-w-6xl mx-auto space-y-4">
        <div>
          <h1 className="text-3xl font-bold">Broadcast Composer</h1>
          <p className="text-slate-500 text-sm">
            Pick a message, language, and photo card. Copy the composed post
            or open WhatsApp directly. Every send is logged for audit.
          </p>
        </div>

        {flash && (
          <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900"
               data-testid="admin-broadcast-flash">{flash}</div>
        )}

        <div className="grid md:grid-cols-3 gap-4">
          {/* Left column — broadcast list */}
          <Card className="md:col-span-1" data-testid="broadcast-list-card">
            <CardHeader>
              <CardTitle className="text-base">10 broadcasts</CardTitle>
              <CardDescription>Sorted by launch order</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1">
              {data.broadcasts.map(b => (
                <button
                  key={b.id}
                  onClick={() => setSelectedId(b.id)}
                  className={`w-full text-left px-3 py-2 rounded text-sm border ${
                    b.id === selectedId
                      ? 'bg-emerald-600 border-emerald-700 text-white'
                      : 'bg-white border-slate-200 hover:bg-slate-100'
                  }`}
                  data-testid={`broadcast-item-${b.id}`}
                >
                  <span className="font-mono text-xs opacity-70">#{b.id}</span>{' '}
                  <span className="font-medium">{b.name}</span>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Right column — composer */}
          <Card className="md:col-span-2" data-testid="broadcast-composer-card">
            <CardHeader>
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <CardTitle>
                  #{current.id} &middot; {current.name}
                </CardTitle>
                <div className="flex gap-1">
                  {LANGS.map(l => (
                    <button
                      key={l.code}
                      onClick={() => setLang(l.code)}
                      className={`px-3 py-1 text-xs rounded-full border ${
                        lang === l.code
                          ? 'bg-emerald-600 text-white border-emerald-700'
                          : 'bg-white border-slate-200 hover:bg-slate-100'
                      }`}
                      data-testid={`broadcast-lang-${l.code}`}
                    >
                      {l.label}
                      {current[`${l.code}_missing`] && (
                        <sup className="ml-1 text-[9px] opacity-70">(EN)</sup>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <textarea
                readOnly
                value={message}
                className="w-full rounded-md border border-slate-200 bg-slate-50 p-3 text-sm font-mono min-h-[200px]"
                data-testid="broadcast-message-textarea"
              />

              {/* Photo card picker */}
              <div>
                <p className="text-xs text-slate-500 mb-2">Photo card to attach</p>
                <div className="grid grid-cols-5 gap-2">
                  {data.photo_cards.map(pc => (
                    <button
                      key={pc.id}
                      onClick={() => setCardId(pc.id)}
                      className={`rounded-lg overflow-hidden border-2 ${
                        pc.id === cardId ? 'border-emerald-500' : 'border-transparent hover:border-slate-300'
                      }`}
                      data-testid={`broadcast-card-${pc.id}`}
                    >
                      <img src={pc.path} alt={pc.name}
                           className="w-full h-16 object-cover" />
                      <div className="text-[10px] py-1 bg-white truncate px-1">{pc.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="text-xs text-slate-500 pt-2">
                Composed length: <span className="font-mono">{composed.length}</span> chars
                {' '}&middot; image URL: <span className="font-mono">{imageUrl || '\u2014'}</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button onClick={copyToClipboard} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="broadcast-copy-btn">
                  📋 Copy text + image URL
                </Button>
                <Button onClick={openInWhatsApp} variant="outline"
                        data-testid="broadcast-open-wa-btn">
                  📱 Open in WhatsApp
                </Button>
                <Button onClick={markSent} variant="outline"
                        className="border-amber-400 text-amber-700 hover:bg-amber-50"
                        data-testid="broadcast-mark-sent-btn">
                  ✓ Mark as sent
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sent-log audit trail */}
        <Card data-testid="broadcast-sent-log-card">
          <CardHeader>
            <CardTitle className="text-base">
              Recent sends
              <Badge className="ml-2 bg-slate-100 text-slate-800">{sentLog.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sentLog.length === 0 ? (
              <p className="text-sm text-slate-500">No broadcasts logged yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 uppercase">
                    <th className="py-1">At</th>
                    <th className="py-1">Broadcast</th>
                    <th className="py-1">Lang</th>
                    <th className="py-1">Card</th>
                    <th className="py-1">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {sentLog.map((s, i) => (
                    <tr key={s.id || i} className="border-t border-slate-100">
                      <td className="py-1 font-mono">{s.at?.slice(0, 16).replace('T', ' ')}</td>
                      <td className="py-1">#{s.broadcast_id}</td>
                      <td className="py-1 uppercase">{s.language}</td>
                      <td className="py-1">{s.card_id ? `#${s.card_id}` : '\u2014'}</td>
                      <td className="py-1 text-slate-500">{s.note || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
